import { cachePersistor, client } from '../App';

export type FetchPolicy =
  | 'cache-first'
  | 'cache-and-network'
  | 'no-cache'
  | 'network-only'
  | 'cache-only'

export type WatchQueryFetchPolicy = FetchPolicy | undefined;

/**
 * Fetch Policy - Cache first
 * @description Da utilizzare per `fetchPolicy` per query di dati che NON cambiano mai. È settata a `cache-first`.
 * @constant
 * @type {string}
 * @example nei parametri `useQuery`
 * useQuery(QUERY, {
 *   fetchPolicy: CACHE_FP_NOT_CHANGING,
 *   nextFetchPolicy: CACHE_NFP_NOT_CHANGING,
 * });
 */
export const CACHE_FP_NOT_CHANGING: WatchQueryFetchPolicy = 'cache-first';

/**
 * Next Fetch Policy - Cache first
 * @description Da utilizzare per `nextFetchPolicy` per query di dati che NON cambiano mai. È settata a `cache-first`.
 * @constant
 * @type {string}
 * @example nei parametri `useQuery`
 * useQuery(QUERY, {
 *   fetchPolicy: CACHE_FP_NOT_CHANGING,
 *   nextFetchPolicy: CACHE_NFP_NOT_CHANGING,
 * });
 */
export const CACHE_NFP_NOT_CHANGING: WatchQueryFetchPolicy = 'cache-first';

/**
 * Fetch Policy - Cache and Network
 * @description Da utilizzare per `fetchPolicy` per query di dati che CAMBIANO. È settata a `cache-and-network`.
 * @constant
 * @type {string}
 * @example nei parametri `useQuery`
 * useQuery(QUERY, {
 *   fetchPolicy: CACHE_FP_CHANGING,
 *   nextFetchPolicy: CACHE_NFP_CHANGING,
 * });
 */
export const CACHE_FP_CHANGING: WatchQueryFetchPolicy = 'cache-and-network';

/**
 * Next Fetch Policy - Cache first
 * @description Da utilizzare per `nextFetchPolicy` per query di dati che non CAMBIANO. È settata a `cache-first`.
 * @constant
 * @type {string}
 * @example nei parametri `useQuery`
 * useQuery(QUERY, {
 *   fetchPolicy: CACHE_FP_CHANGING,
 *   nextFetchPolicy: CACHE_NFP_CHANGING,
 * });
 */
export const CACHE_NFP_CHANGING: WatchQueryFetchPolicy = 'cache-and-network';

export const CACHE_FIRST = {
  fetchPolicy: CACHE_FP_NOT_CHANGING,
  nextFetchPolicy: CACHE_NFP_NOT_CHANGING,
};

export const CACHE_AND_NETWORK = {
  fetchPolicy: CACHE_FP_CHANGING,
  nextFetchPolicy: CACHE_NFP_CHANGING,
};

export const CACHE_AND_NETWORK_CACHE_FIRST = {
  fetchPolicy: CACHE_FP_CHANGING,
  nextFetchPolicy: CACHE_NFP_NOT_CHANGING,
};

export const CACHE_FIRST_CACHE_AND_NETWORK = {
  fetchPolicy: CACHE_FP_NOT_CHANGING,
  nextFetchPolicy: CACHE_NFP_CHANGING,
};

/**
 * Asynchronously clears all application caches and resets the application state.
 *
 * This function performs the following tasks:
 * - Pauses the cache persistor to prevent further interactions during cleanup.
 * - Clears the Apollo client store.
 * - Empties the browser's `localStorage`.
 * - Purges the Apollo cache persistor completely.
 * - Logs the size of the persisted cache after clearing.
 * - Deletes all remaining IndexedDB databases.
 * - Reloads the application by redirecting the user to the root ('/') after a brief delay.
 *
 * This is intended for use in scenarios where the complete application state needs to be reset.
 */
export const clearAllCaches = async () => {
  console.log('clearing cache...');

  cachePersistor.pause(); // pause cache persistor

  await client.clearStore(); // clear Apollo client cache
  localStorage.clear(); // clear LocalStorage
  await cachePersistor.purge(); // clear apollo cache persistor

  console.log('persisted cache cleared successfully, size: ', await cachePersistor.getSize());

  // clears all remaining indexedDb databases
  const dbs = await window.indexedDB.databases()
  dbs.forEach(db => { db.name && window.indexedDB.deleteDatabase(db.name) })

  setTimeout(() => {
    window.location.href = '/';
  }, 500)
}
